import { alpha, Avatar, Button, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import { useTranslation } from 'react-i18next';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useState } from 'react';
import { EditCommandsEnum } from '@diana/core';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { useAiAcceptRejectCommands } from '../../Support/useAiAcceptRejectCommands';

export const AiPendingChangesModalContent = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();

  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);

  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);

  const setModalClose = () => {
    setActiveModal(null);
  };

  const resetLoading = () => {
    setIsAccepting(false);
    setIsRejecting(false);
  };

  const { updatePendingStatus } = useAiAcceptRejectCommands({
    onFinally: resetLoading,
    onSuccess: setModalClose,
  });

  const handleAccept = () => {
    setIsAccepting(true);
    updatePendingStatus({ command: EditCommandsEnum.Accept });
  };

  const handleDiscard = () => {
    setIsRejecting(true);
    updatePendingStatus({ command: EditCommandsEnum.Decline, updateCache: true });
  };

  return (
    <Stack
      p={4}
      sx={{
        maxWidth: 532,
        width: '100%',
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        borderRadius: 1.5,
      }}
    >
      <Avatar
        sx={{
          mb: 2,
          border: `1.5px solid ${alpha(palette.primary.main, 0.2)}`,
          backgroundColor: alpha(palette.primary.main, 0.1),
        }}
      >
        <ElectricBoltOutlinedIcon color="primary" fontSize="small" />
      </Avatar>
      <Typography sx={{ mb: 3, ml: 1 }}>{t('descriptions.needAiPendingAction')}</Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Button
          onClick={setModalClose}
          color="secondary"
          sx={{
            p: 1,
            color: palette.text.secondary,
            '&:hover': { backgroundColor: palette.action.hover },
          }}
        >
          {t('labels.continueEditing')}
        </Button>
        <Stack direction="row" alignItems="center" gap={1}>
          <LoadingButton
            loading={isRejecting}
            variant="contained"
            color="secondary"
            onClick={handleDiscard}
          >
            {t('labels.discard')}
          </LoadingButton>
          <LoadingButton loading={isAccepting} variant="contained" onClick={handleAccept}>
            {t('labels.accept')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
